import { useNavigate } from "react-router-dom";
import { PortalSidebar, type SidebarNavSection } from "@/shared/layout/PortalSidebar";
import { ProfileSwitcher } from "./ProfileSwitcher";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { useQuery } from "@tanstack/react-query";
import { reviewListQuery } from "@/portals/patient/api/patientQueries";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import type { PatientProfile } from "@/portals/patient/api/patientProfileApi";
import { MedSyncWordmark } from "@/shared/components/MedSyncWordmark";
import { QUEUE_PATHS, countAwaiting, type ReviewQueue } from "@/features/patient/human-review/reviewQueues";

const NO_COUNTS: Record<ReviewQueue, number> = { health: 0, identity: 0 };

interface PatientSidebarProps {
  profile: PatientProfile;
}

export const navSections: SidebarNavSection[] = [
  {
    title: "Overview",
    items: [
      { path: "/patient", label: "Dashboard", icon: "lucide:layout-dashboard", end: true },
      { path: "/patient/upload", label: "Upload Records", icon: "lucide:upload" },
      { path: "/patient/documents", label: "Document Panel", icon: "lucide:folder-open" },
      { path: "/patient/human-review-queue", label: "Human Review Queue", icon: "lucide:user-check" },
      // Identity checks ("Is this upload yours?"): only the patient answers them.
      { path: "/patient/patient-only-queue", label: "Patient Only Queue", icon: "lucide:user-search" },
      { path: "/patient/health-concerns", label: "Health Concerns", icon: "lucide:alert-triangle" },
      { path: "/patient/timeline", label: "Health Timeline", icon: "lucide:clock" },
      { path: "/patient/ask", label: "Ask About Records", icon: "lucide:message-circle-question" },
      { path: "/patient/consent-privacy", label: "Consent & Privacy", icon: "lucide:shield-check" },
    ],
  },
  // Family & Caregivers section hidden from the sidebar — caregiver/dedicated-profile
  // module is temporarily hidden from end users. Routes still exist, just not linked.
  // {
  //   title: "Family & Caregivers",
  //   items: [
  //     { path: "/patient/manage-family", label: "Manage Family", icon: "lucide:users" },
  //     { path: "/patient/manage-caregivers", label: "Manage Caregivers", icon: "lucide:user-cog" },
  //     { path: "/caregiver", label: "Caregiver Dashboard", icon: "lucide:heart-handshake" },
  //   ],
  // },
  {
    title: "Health Records",
    items: [
      { path: "/patient/diagnosis", label: "Diagnoses", icon: "lucide:stethoscope" },
      { path: "/patient/complete-history", label: "Complete History", icon: "lucide:history" },
      { path: "/patient/medications", label: "Medications", icon: "lucide:pill" },
      { path: "/patient/diagnostics", label: "Diagnostics & Imaging", icon: "lucide:test-tube" },
      { path: "/patient/care-treatment", label: "Care & Treatment", icon: "lucide:clipboard-list" },
    ],
  },
  {
    title: "Insurance & Billing",
    items: [
      { path: "/patient/insurance-cards", label: "Insurance & Cards", icon: "lucide:shield" },
      { path: "/patient/claims", label: "Claims", icon: "lucide:receipt" },
    ],
  },
  {
    title: "Health Exchange",
    items: [
      { path: "/patient/fhir-export", label: "FHIR Export", icon: "lucide:file-down" },
    ],
  },
];

// Items awaiting the patient show as a count on their own queue: health items on
// the Human Review Queue, identity checks on the Patient Only Queue.
export function withReviewBadges(sections: SidebarNavSection[], counts: Record<ReviewQueue, number>): SidebarNavSection[] {
  const badgeFor: Record<string, number> = {
    [QUEUE_PATHS.health.page]: counts.health,
    [QUEUE_PATHS.identity.page]: counts.identity,
  };
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) => (badgeFor[item.path] > 0 ? { ...item, badge: badgeFor[item.path] } : item)),
  }));
}

export function PatientSidebar({ profile }: PatientSidebarProps) {
  const signOut = usePatientAuthStore((s) => s.signOut);
  const navigate = useNavigate();
  const { activeDependent } = useFamily();
  // Counted from the same list the Human Review Queue shows, so the badge and
  // the queue can never disagree. A failed read keeps the last known value
  // rather than flashing the badge away.
  const { data: counts = NO_COUNTS } = useQuery({
    ...reviewListQuery(activeDependent?.id),
    select: (items) => ({ health: countAwaiting(items, "health"), identity: countAwaiting(items, "identity") }),
  });

  return (
    <PortalSidebar
      logoTitle={<MedSyncWordmark />}
      logoSubtitle="Healthcare Platform"
      profileSlot={<ProfileSwitcher profile={profile} />}
      navSections={withReviewBadges(navSections, counts)}
      onSignOut={() => {
        // Home page after logout (navigate first so sign-in never flashes).
        navigate("/about", { replace: true });
        void signOut();
      }}
    />
  );
}
